import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { typographyVariants, type TypographyTone } from './typography'

type AlertVariantProps = VariantProps<typeof alertVariants>
export type AlertVariant = NonNullable<AlertVariantProps['variant']>
export type AlertLayout = NonNullable<AlertVariantProps['layout']>

type AlertStyle = {
  layout: AlertLayout
  variant: AlertVariant
}

const AlertStyleContext = React.createContext<AlertStyle>({
  layout: 'inline',
  variant: 'default',
})

const alertVariants = cva(
  'relative w-full items-start rounded-md border [&>svg]:size-4 [&>svg]:translate-y-0.5',
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        neutral: 'border-border/20 bg-card text-foreground dark:border-white/10 dark:bg-card/70',
        destructive:
          'border-status-destructive-border bg-status-destructive-background text-foreground [&>svg]:text-status-destructive-foreground',
        info: 'border-status-info-border bg-status-info-background text-foreground [&>svg]:text-status-info-foreground',
        warning:
          'border-status-warning-border bg-status-warning-background text-foreground [&>svg]:text-status-warning-foreground',
        success:
          'border-status-success-border bg-status-success-background text-foreground [&>svg]:text-status-success-foreground',
        brand: 'border-primary/25 bg-primary/5 text-foreground [&>svg]:text-interaction',
      },
      layout: {
        inline:
          'grid grid-cols-[0_1fr] gap-y-0.5 px-4 py-3 text-sm has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-3',
        notice: 'grid grid-cols-1 gap-0 overflow-hidden p-0 shadow-none',
      },
    },
    defaultVariants: {
      variant: 'default',
      layout: 'inline',
    },
  }
)

const alertTitleTone: Record<AlertVariant, TypographyTone> = {
  brand: 'interaction',
  default: 'default',
  destructive: 'destructive',
  info: 'info',
  neutral: 'default',
  success: 'success',
  warning: 'warning',
}

const alertDescriptionTone: Partial<Record<AlertVariant, TypographyTone>> = {
  neutral: 'muted',
}

const alertTitleLayout: Record<AlertLayout, string> = {
  inline: 'col-start-2 line-clamp-1 min-h-4 tracking-tight',
  notice: 'col-start-auto min-h-0',
}

const alertDescriptionLayout: Record<AlertLayout, string> = {
  inline: 'col-start-2',
  notice: 'col-start-auto',
}

export type AlertProps = React.ComponentProps<'div'> & Partial<AlertStyle>

function Alert({ className, layout = 'inline', variant = 'default', ...props }: AlertProps) {
  return (
    <AlertStyleContext.Provider value={{ layout, variant }}>
      <div
        data-slot="alert"
        data-layout={layout}
        data-variant={variant}
        role="alert"
        className={cn(alertVariants({ layout, variant }), className)}
        {...props}
      />
    </AlertStyleContext.Provider>
  )
}

export type AlertTitleProps = React.ComponentProps<'div'> & {
  tone?: TypographyTone
}

function AlertTitle({ className, tone, ...props }: AlertTitleProps) {
  const context = React.useContext(AlertStyleContext)
  const resolvedTone = tone ?? alertTitleTone[context.variant] ?? 'default'

  return (
    <div
      data-slot="alert-title"
      data-tone={resolvedTone}
      className={cn(
        typographyVariants({ variant: 'labelStrong', tone: resolvedTone }),
        alertTitleLayout[context.layout] ?? alertTitleLayout.inline,
        className
      )}
      {...props}
    />
  )
}

export type AlertDescriptionProps = React.ComponentProps<'div'> & {
  tone?: TypographyTone
}

function AlertDescription({ className, tone, ...props }: AlertDescriptionProps) {
  const context = React.useContext(AlertStyleContext)
  const resolvedTone = tone ?? alertDescriptionTone[context.variant] ?? 'default'

  return (
    <div
      data-slot="alert-description"
      data-tone={resolvedTone}
      className={cn(
        typographyVariants({ variant: 'description', tone: resolvedTone }),
        'grid justify-items-start gap-1 [&_p]:leading-relaxed',
        alertDescriptionLayout[context.layout] ?? alertDescriptionLayout.inline,
        className
      )}
      {...props}
    />
  )
}

export { Alert, AlertTitle, AlertDescription }
